import { CompanyDataFormValues, financialReportLabel } from './company-data.schema';

interface LivePreviewCardProps {
  data: Partial<CompanyDataFormValues>;
}

const formatPrice = (min: string, max: string) => {
  const fmt = (v: string) => {
    const num = v.replace(/\D/g, '');
    if (!num) return '';
    return `Rp ${new Intl.NumberFormat('id-ID').format(Number(num))}`;
  };
  const fMin = fmt(min);
  const fMax = fmt(max);
  if (fMin && fMax) return `${fMin} - ${fMax}`;
  if (fMin) return fMin;
  return '-';
};

export const LivePreviewCard = ({ data }: LivePreviewCardProps) => {
  const businessName = data.business_name || 'Nama Bisnis';
  const industry = data.industry || '';
  const businessCategory = data.business_category || '';
  const legalEntity = data.legal_entity || '';

  const ownerName = data.client_name || '-';
  const ownerPhone = data.owner_phone || '-';
  const domicile =
    data.owner_citizenship === 'WNA'
      ? data.owner_foreign_domicile || '-'
      : data.regency_name || data.province_name || '-';
  const legalName = data.business_legal_name || '-';

  const businessAge = data.business_age_years ? `${data.business_age_years} Tahun` : '-';
  // A cleared input reads '' here, which must render '-' — but a real 0 still shows "0 Cabang".
  const branchCount =
    data.branch_count !== undefined && data.branch_count !== null && String(data.branch_count) !== ''
      ? `${data.branch_count} Cabang`
      : '-';
  const totalEmployees = data.total_employees ? `${data.total_employees} Orang` : '-';
  const businessScale = data.business_scale || '-';
  let ownershipStatus = '-';
  if (data.ownership_status === 'Syirkah' && data.partnership_count) {
    ownershipStatus = `Syirkah (${data.partnership_count} Mitra)`;
  } else if (data.ownership_status) {
    ownershipStatus = data.ownership_status;
  }
  const hasFinancialReport = data.has_financial_report ? financialReportLabel(data.has_financial_report) : '-';
  let businessLocation: string;
  if (data.business_in_indonesia === 'Tidak') {
    businessLocation = data.business_foreign_location || '-';
  } else if (data.business_regency_name && data.business_province_name) {
    businessLocation = `${data.business_regency_name}, ${data.business_province_name}`;
  } else {
    businessLocation = data.business_province_name || '-';
  }

  const officePhone = data.office_phone || '';
  const businessWebsite = data.business_website || '';
  const contact = [officePhone, businessWebsite].filter(Boolean).join(' / ') || '-';
  const headOfficeAddress = data.head_office_address || '-';

  const mainProblems = [data.main_problem_1, data.main_problem_2, data.main_problem_3].filter(Boolean);

  const primaryProduct = data.product_services_primary || '-';
  const socialMediaAccounts = (data.social_media_marketplaces || []).filter(
    s => s.social_media_marketplace && s.account_name,
  );
  const otherProducts = (data.other_product_services || []).filter(p => p.product_service_name);

  return (
    <div
      className="rounded-2xl overflow-hidden text-white text-sm shadow-xl"
      style={{ background: 'linear-gradient(115deg, #1848FD 0%, #1330a6 55%, #071753 100%)' }}
    >
      {/* Header */}
      <div className="px-4 pt-4 pb-3">
        <div className="flex items-start justify-between gap-2">
          <div>
            <div className="flex items-center gap-2">
              <div
                className="w-8 h-8 rounded-md flex items-center justify-center text-xs font-bold text-white shrink-0"
                style={{ background: 'rgba(255,255,255,0.2)' }}
              >
                AOS
              </div>
              <div>
                <p className="font-bold text-base leading-tight">{businessName}</p>
              </div>
            </div>
            <div className="flex flex-wrap gap-1 mt-2">
              {industry && (
                <span
                  className="text-xs px-2 py-0.5 rounded-full font-medium"
                  style={{ background: 'rgba(255,255,255,0.15)' }}
                >
                  {industry}
                </span>
              )}
              {businessCategory && (
                <span
                  className="text-xs px-2 py-0.5 rounded-full font-medium"
                  style={{ background: 'rgba(255,255,255,0.15)' }}
                >
                  {businessCategory}
                </span>
              )}
            </div>
          </div>
          {legalEntity && (
            <span className="text-xs px-2 py-1 rounded-md font-bold shrink-0" style={{ background: '#FE8D16' }}>
              {legalEntity}
            </span>
          )}
        </div>
      </div>

      {/* Section: Identitas Pemilik & Bisnis */}
      <Section title="IDENTITAS PEMILIK & BISNIS">
        <Row label="Pemilik" value={ownerName} />
        <Row label="WhatsApp" value={ownerPhone} />
        <Row label="Domisili" value={domicile} />
        <Row label="Nama Legal" value={legalName} />
      </Section>

      {/* Section: Operasional & Skala Usaha */}
      <Section title="OPERASIONAL & SKALA USAHA">
        <Row label="Berdiri" value={businessAge} />
        <Row label="Cabang" value={branchCount} />
        <Row label="Karyawan" value={totalEmployees} />
        <Row label="Omset" value={businessScale} />
        <Row label="Kepemilikan" value={ownershipStatus} />
        <Row label="PSAK" value={hasFinancialReport} />
        <Row label="Lokasi" value={businessLocation} />
        <Row label="Alamat Pusat" value={headOfficeAddress} />
        <Row label="Kontak" value={contact} />
      </Section>

      {/* Section: 3 Masalah Utama */}
      <Section title="3 MASALAH UTAMA BISNIS">
        {mainProblems.length > 0 ? (
          <ol className="list-none space-y-1">
            {mainProblems.map((p, i) => (
              <li key={i} className="text-xs" style={{ color: '#82B2FA' }}>
                <span className="text-white font-medium">{i + 1}.</span> <span className="text-white">{p}</span>
              </li>
            ))}
          </ol>
        ) : (
          <p className="text-xs" style={{ color: '#82B2FA' }}>
            Belum diisi
          </p>
        )}
      </Section>

      {/* Section: Produk & Sosial Media */}
      <Section title="PRODUK & SOSIAL MEDIA">
        <p className="text-xs mb-2">
          <span style={{ color: '#82B2FA' }}>Produk Utama: </span>
          <span className="font-semibold">{primaryProduct}</span>
        </p>

        {socialMediaAccounts.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-2">
            {socialMediaAccounts.map((s, i) => (
              <span
                key={i}
                className="text-xs px-2 py-0.5 rounded-full"
                style={{ background: 'rgba(255,255,255,0.15)' }}
              >
                {s.social_media_marketplace}: {s.account_name}
              </span>
            ))}
          </div>
        )}

        {otherProducts.length > 0 && (
          <div className="space-y-1">
            {otherProducts.map((p, i) => (
              <div key={i} className="flex justify-between items-center text-xs">
                <span style={{ color: '#82B2FA' }}>{p.product_service_name}</span>
                <span className="font-medium">
                  {formatPrice(p.estimated_price_min || '', p.estimated_price_max || '')}
                </span>
              </div>
            ))}
          </div>
        )}
      </Section>
    </div>
  );
};

const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <div className="px-4 py-3" style={{ borderTop: '1px solid rgba(255,255,255,0.12)' }}>
    <p className="text-xs font-bold mb-2 tracking-wider" style={{ color: '#82B2FA' }}>
      {title}
    </p>
    {children}
  </div>
);

const Row = ({ label, value }: { label: string; value: string }) => (
  <div className="flex gap-2 text-xs mb-1">
    <span className="w-24 shrink-0" style={{ color: '#82B2FA' }}>
      {label}
    </span>
    <span className="font-medium">: {value}</span>
  </div>
);
